import { LifeBuoy, BadgeCheck, Car, Shield, MessageSquareText } from 'lucide-react';
import { EC_GREEN } from '@/components/ai/ChatShowcase';
import { GreenHeader } from '@/components/ai/AiSections';
import { openChatWidget } from '@/components/ai/openChatWidget';

const STATS = [
  { value: '51', label: 'Models across every category' },
  { value: '14', label: 'Branches across the UAE' },
  { value: '25,000+', label: 'Happy customers' },
  { value: '1978', label: 'Europcar in the UAE since' },
];

const PROMISES = [
  { icon: LifeBuoy, title: '24/7 Roadside', desc: 'Assistance anywhere in the UAE, any hour, on 800 EUROPCAR.' },
  { icon: BadgeCheck, title: 'No Hidden Charges', desc: 'Deposit, VAT and fees disclosed before you pay, and explained by the concierge.' },
  { icon: Car, title: 'A Car for Every Journey', desc: 'A wide, well-kept choice of models, always a great fit for the trip ahead.' },
  { icon: Shield, title: 'Comprehensive Cover', desc: 'Third-party cover over 1 million USD, with CDW on every rental.' },
];

export function TrustStrip() {
  return (
    <section data-testid="ai-stats" className="bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 divide-x divide-black/5 border-b border-black/5 lg:grid-cols-4">
          {STATS.map((s) => (
            <div key={s.label} className="px-4 py-7 text-center">
              <p className="font-heading font-black text-3xl sm:text-4xl tracking-tight leading-none" style={{ color: EC_GREEN }}>{s.value}</p>
              <p className="mt-2 font-body text-xs sm:text-sm text-[#666666]">{s.label}</p>
            </div>
          ))}
        </div>
        <div data-testid="ai-promises" className="grid grid-cols-1 gap-4 py-10 sm:grid-cols-2 lg:grid-cols-4">
          {PROMISES.map((p) => (
            <div key={p.title} className="flex gap-4 rounded-2xl border border-black/[0.06] bg-white p-5">
              <span className="grid h-11 w-11 flex-shrink-0 place-items-center rounded-xl bg-[#E8F5EC]" style={{ color: EC_GREEN }}><p.icon className="w-5 h-5" /></span>
              <div>
                <h3 className="font-heading font-bold text-base text-[#121212]">{p.title}</h3>
                <p className="mt-1 font-body text-sm text-[#666666] leading-relaxed">{p.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const JOURNEYS = [
  { id: 'escape', eyebrow: 'Weekend escapes', title: 'Chase the horizon', desc: 'Point the car at Hatta, Jebel Jais or the East Coast and just go. Unlimited UAE miles, roadside cover, and a 4x4 ready when you are.', ask: 'I need a 4x4 for two days to Jebel Jais this weekend.', img: '/ai/journey-escape.jpg' },
  { id: 'city', eyebrow: 'Every day, sorted', title: 'The city, on your terms', desc: 'Skip the queues and surge fares. A car at your door, no deposit tying up your card, ready for the school run, the meeting, or a drive along the coast.', ask: 'Which compact car can be delivered to Dubai Marina tomorrow morning?', img: '/ai/journey-city.jpg' },
  { id: 'stay', eyebrow: 'Long stays & new starts', title: 'Feel at home, faster', desc: 'New to the Emirates? Go monthly for a lower rate with maintenance included, and the freedom to hand the keys back the moment your plans change.', ask: 'What does a 3-month SUV rental cost with delivery to Arabian Ranches?', img: '/ai/journey-stay.jpg' },
];

export function JourneyCards() {
  return (
    <section data-testid="ai-journeys" className="bg-white py-16 sm:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <GreenHeader chip="Made for the moments that matter" title="Your journey, your way." desc="Whatever the Emirates has in store, a spontaneous escape, the daily run, or a long stay far from home, there is a Europcar that fits the moment. Just ask." />
        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
          {JOURNEYS.map((j) => (
            <article key={j.id} data-testid={`ai-journey-${j.id}`} className="group flex flex-col overflow-hidden rounded-3xl border border-black/[0.06] bg-white">
              <div className="aspect-[4/3] overflow-hidden"><img src={j.img} alt={j.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" /></div>
              <div className="flex flex-1 flex-col p-6">
                <p className="font-heading font-bold text-[11px] tracking-[0.15em] uppercase" style={{ color: EC_GREEN }}>{j.eyebrow}</p>
                <h3 className="mt-2 font-heading font-bold text-xl text-[#121212]">{j.title}</h3>
                <p className="mt-2 flex-1 font-body text-sm text-[#666666] leading-relaxed">{j.desc}</p>
                <button type="button" data-testid={`ai-journey-ask-${j.id}`} onClick={() => openChatWidget(j.ask)} className="mt-5 flex min-h-[44px] items-start gap-3 rounded-2xl bg-[#F1F8F3] px-4 py-3 text-left transition-colors hover:bg-[#E8F5EC]">
                  <MessageSquareText className="mt-0.5 w-4 h-4 flex-shrink-0" style={{ color: EC_GREEN }} />
                  <span className="font-body text-sm text-[#121212]"><span className="font-heading font-bold text-[11px] tracking-wider uppercase" style={{ color: EC_GREEN }}>Try asking · one tap</span><br />“{j.ask}”</span>
                </button>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
